/**
 * ContactTabBar — the 6-tab strip for the Contact record (P1, 2026-07-06).
 *
 * This is the L1A `StageTabBar` mechanism (ui.tsx) applied to a contact tab
 * model: SAME `.stagebar`/`.nav` classes, SAME `role="tablist"` + roving
 * tabindex + ArrowLeft/Right/Home/End (Home/End jump to ends) keyboard model,
 * SAME mobile horizontal chip-rail (the `.stagebar` `@media max-width:1080px`
 * block in components.css). A `.contact-tabs` modifier (contact-record.css)
 * swaps the L1A status-dot for an icon and re-points the sticky offset.
 *
 * It is a THIN, contact-specific bar rather than a direct reuse of StageTabBar
 * only because StageTabBar's item model is the fixed L1A stage chain (status
 * dots, meta bookends). The behaviour is copied verbatim so the two feel
 * identical and the proven mobile swipe-rail carries over unchanged.
 */
import { useRef, type KeyboardEvent } from 'react';

export type ContactTabKey =
  | 'details'
  | 'birth'
  | 'groups'
  | 'projects'
  | 'enquiries'
  | 'comms'
  | 'astro';

export interface ContactTabItem {
  key: ContactTabKey;
  label: string;
  /** Emoji icon rendered in the `.dot` slot (contact tabs are icon tabs). */
  icon: string;
}

export const CONTACT_TABS: ContactTabItem[] = [
  { key: 'details', label: 'Contact details', icon: '👤' },
  { key: 'birth', label: 'Birth details', icon: '🌅' },
  { key: 'groups', label: 'Groups', icon: '👪' },
  { key: 'projects', label: 'Projects', icon: '🏗️' },
  { key: 'enquiries', label: 'Enquiries', icon: '💬' },
  { key: 'comms', label: 'Communications', icon: '📨' },
  { key: 'astro', label: 'Astro', icon: '🔯' },
];

export function ContactTabBar({
  items = CONTACT_TABS,
  activeKey,
  onSelect,
  panelId = 'contact-panel',
}: {
  items?: ContactTabItem[];
  activeKey: ContactTabKey;
  onSelect: (key: ContactTabKey) => void;
  /** id of the single tabpanel this strip controls (aria-controls). */
  panelId?: string;
}) {
  const keys = items.map((i) => i.key);
  // Refs to each tab button so keyboard nav can MOVE DOM focus to the newly
  // selected tab (the mockup does `btn.focus()`). Without this, focus strands
  // on the old tab which becomes tabindex=-1 (Vera HIGH a11y gate-blocker).
  const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const idx = keys.indexOf(activeKey);
    if (idx < 0) return;
    let next = -1;
    if (e.key === 'ArrowRight') next = (idx + 1) % keys.length;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + keys.length) % keys.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = keys.length - 1;
    if (next >= 0) {
      e.preventDefault();
      onSelect(keys[next]);
      // Move focus to the new tab (it exists already — only its roving
      // tabindex flips on the re-render) and scroll it into the strip's rail.
      const btn = btnRefs.current[next];
      if (btn) {
        btn.focus();
        btn.scrollIntoView({ inline: 'center', block: 'nearest' });
      }
    }
  }

  return (
    <div
      className="stagebar contact-tabs"
      role="tablist"
      aria-orientation="horizontal"
      aria-label="Contact record sections"
      onKeyDown={onKeyDown}
    >
      {items.map((it, i) => {
        const active = it.key === activeKey;
        return (
          <button
            key={it.key}
            ref={(el) => {
              btnRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`ctab-${it.key}`}
            aria-selected={active}
            aria-controls={panelId}
            tabIndex={active ? 0 : -1}
            className={`nav${active ? ' on' : ''}`}
            title={it.label}
            onClick={(e) => {
              onSelect(it.key);
              // Match the L1A/mockup behaviour: scroll the active chip into
              // view within the strip's OWN horizontal rail (the one allowed
              // scroll box), never the page.
              (e.currentTarget as HTMLElement).scrollIntoView({
                inline: 'center',
                block: 'nearest',
              });
            }}
          >
            <span className="dot" aria-hidden>
              {it.icon}
            </span>
            <span className="lbl">{it.label}</span>
          </button>
        );
      })}
    </div>
  );
}
